import { useState, useEffect } from 'react'
import { useInviteMember } from '../../hooks/useOrganization'

interface InviteMemberModalProps {
  isOpen: boolean
  onClose: () => void
}

export function InviteMemberModal({ isOpen, onClose }: InviteMemberModalProps) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'admin' | 'editor' | 'viewer'>('editor')
  const inviteMember = useInviteMember()

  // ESCキーでモーダルを閉じる
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email.trim()) {
      alert('メールアドレスを入力してください')
      return
    }

    try {
      const result = await inviteMember.mutateAsync({ email: email.trim(), role })
      
      // 成功メッセージを表示
      if (result.type === 'existing_user') {
        alert('既存ユーザーを組織に追加しました')
      } else {
        alert('招待メールを送信しました。ユーザーが登録すると組織に参加します。')
      }
      
      setEmail('')
      setRole('editor')
      onClose()
    } catch (error) {
      alert(error instanceof Error ? error.message : 'メンバーの招待に失敗しました')
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
    >
      <div
        className="modal-card"
        style={{ maxWidth: 480 }}
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="modal-header">
            <h2 className="text-lg font-semibold">メンバーを招待</h2>
            <button type="button" className="close-btn" onClick={onClose} aria-label="閉じる">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">
                メールアドレス
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input"
                placeholder="user@example.com"
                required
              />
              <p className="form-hint">
                未登録の場合は登録用のリンクが記載された招待メールが送信されます
              </p>
              <p className="form-hint">
                既存ユーザーの場合は自動的に組織に追加され、通知メールが送信されます
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">
                ロール
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as 'admin' | 'editor' | 'viewer')}
                className="form-select"
              >
                <option value="viewer">閲覧者 - 議事録の閲覧のみ</option>
                <option value="editor">編集者 - 議事録の作成・編集が可能</option>
                <option value="admin">管理者 - すべての操作が可能</option>
              </select>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
              disabled={inviteMember.isPending}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={inviteMember.isPending}
            >
              {inviteMember.isPending ? '招待中...' : '招待する'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
